import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './auth/AuthContext';
import { formatDate } from './utils/formatDate';
import { CALL_STATUS } from './utils/constants';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const REFUND_PAGE_SIZE = 20;

const quickActions = [
  { label: 'Invite new mentor', description: 'Create a mentor account and send onboarding material.', path: '/mentors' },
  { label: 'Review pending calls', description: 'Check the latest uploads for quality assurance.', path: '/calls' },
  { label: 'View all students', description: 'Browse the full student roster and assignments.', path: '/students' },
];

const MetricCard = ({ title, value, change, changeType, onClick }) => (
  <div
    className={`card metric-card animate-fade-in ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <p className="metric-label">{title}</p>
    <p className="metric-value">{value}</p>
    {change && (
      <span className={`metric-change ${changeType}`}>
        {changeType === 'positive' ? '↑' : changeType === 'negative' ? '↓' : ''} {change}
      </span>
    )}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function Dashboard() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [refundRequests, setRefundRequests] = useState({ count: 0, students: [] });
  const [showRefundRequests, setShowRefundRequests] = useState(false);
  const [refundPage, setRefundPage] = useState(0);
  const [adminStats, setAdminStats] = useState({
    activeMentors: 0,
    totalStudentsAssigned: 0,
    mentorsThisWeek: 0,
    studentsThisMonth: 0
  });
  const [callMetrics, setCallMetrics] = useState({
    totalCalls: 0,
    connectedCalls: 0,
    callsThisWeek: 0,
    connectionRate: 0,
    unengagedStudents: 0,
    atRiskStudents: 0
  });
  const [weeklyTrend, setWeeklyTrend] = useState([]);
  const [courseDistribution, setCourseDistribution] = useState([]);
  const [topMentors, setTopMentors] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAdminStats = useCallback(async () => {
    try {
      setLoading(true);
      const now = new Date();
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);

      const weekAgoStr = weekAgo.toISOString();

      // Use server-side counts (not limited by row cap) and only fetch rows we need
      const [
        { count: totalMentors },
        { count: mentorsThisWeek },
        { count: totalStudentsAssigned },
        { count: studentsThisMonth },
        { count: totalCalls },
        { count: connectedCalls },
        { count: callsThisWeek },
        recentCallsData,
        studentsData,
        mentorsData
      ] = await Promise.all([
        supabase.from('mentors').select('*', { count: 'exact', head: true }),
        supabase.from('mentors').select('*', { count: 'exact', head: true }).gte('created_at', weekAgoStr),
        supabase.from('students').select('*', { count: 'exact', head: true }).not('mentor_id', 'is', null),
        supabase.from('students').select('*', { count: 'exact', head: true }).not('mentor_id', 'is', null).gte('created_at', monthAgo.toISOString()),
        supabase.from('calls').select('*', { count: 'exact', head: true }),
        supabase.from('calls').select('*', { count: 'exact', head: true }).eq('call_status', CALL_STATUS.CONNECTED),
        supabase.from('calls').select('*', { count: 'exact', head: true }).gte('created_at', weekAgoStr),
        supabase.from('calls').select('call_status, created_at, mentor_id').gte('created_at', weekAgoStr).order('created_at', { ascending: false }).limit(5000),
        supabase.from('students').select('id, mentor_id, course'),
        supabase.from('mentors').select('id, full_name, email')
      ]);

      const recentCalls = recentCallsData?.data || [];
      if (recentCallsData?.error) console.error('Error fetching recent calls:', recentCallsData.error);
      if (studentsData?.error) console.error('Error fetching students:', studentsData.error);
      const allStudents = studentsData?.data || [];

      setAdminStats({
        activeMentors: totalMentors || 0,
        totalStudentsAssigned: totalStudentsAssigned || 0,
        mentorsThisWeek: mentorsThisWeek || 0,
        studentsThisMonth: studentsThisMonth || 0
      });

      setCallMetrics({
        totalCalls: totalCalls || 0,
        connectedCalls: connectedCalls || 0,
        callsThisWeek: callsThisWeek || 0,
        connectionRate: totalCalls > 0 ? Math.round(((connectedCalls || 0) / totalCalls) * 100) : 0,
        unengagedStudents: 0,
        atRiskStudents: 0
      });

      // Weekly trend from the fetched recent calls (server-filtered to last 7 days)
      const trend = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
        const dayCalls = recentCalls.filter(c => {
          const callDate = new Date(c.created_at);
          return callDate >= dayStart && callDate < dayEnd;
        });
        trend.push({
          date: date.toLocaleDateString('en-US', { weekday: 'short' }),
          calls: dayCalls.length,
          connected: dayCalls.filter(c => c.call_status === CALL_STATUS.CONNECTED).length
        });
      }
      setWeeklyTrend(trend);

      // Course distribution
      const courseCounts = {};
      allStudents.forEach(s => {
        if (s.course) {
          courseCounts[s.course] = (courseCounts[s.course] || 0) + 1;
        }
      });
      const shortenCourse = (name) => {
        if (name.includes('Integrated')) return name.includes('JEE') ? 'JEE Int.' : 'NEET Int.';
        if (name.includes('Repeater')) return name.includes('JEE') ? 'JEE Rep.' : 'NEET Rep.';
        return name.length > 12 ? name.substring(0, 12) + '…' : name;
      };
      const dist = Object.entries(courseCounts)
        .map(([name, value]) => ({ name: shortenCourse(name), fullName: name, value }))
        .sort((a, b) => b.value - a.value);
      setCourseDistribution(dist);

      // Top mentors (from recent calls, already filtered to last 7 days)
      const mentorCalls = {};
      recentCalls.forEach(c => {
        if (c.mentor_id) {
          mentorCalls[c.mentor_id] = (mentorCalls[c.mentor_id] || 0) + 1;
        }
      });
      const sortedMentors = (mentorsData?.data || [])
        .map(m => ({ ...m, callCount: mentorCalls[m.id] || 0 }))
        .sort((a, b) => b.callCount - a.callCount)
        .slice(0, 5);
      setTopMentors(sortedMentors);

    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (role === 'admin') {
      fetchRefundRequests();
      fetchAdminStats();
    }
  }, [role, fetchAdminStats]);

  const fetchRefundRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('calls')
        .select('student_id, created_at, students(name, phone)')
        .eq('tags', 'refund_requested')
        .order('created_at', { ascending: true });
      
      if (error) throw error;

      const studentMap = {};
      data.forEach(call => {
        const sid = call.student_id;
        if (!studentMap[sid]) {
          studentMap[sid] = {
            id: sid,
            name: call.students?.name || 'Unknown',
            phone: call.students?.phone || '',
            requestDate: call.created_at
          };
        }
      });
      setRefundRequests({ count: Object.keys(studentMap).length, students: Object.values(studentMap) });
    } catch (error) {
      console.error('Error fetching refund requests:', error);
    }
  };

  const metricCards = [
    { title: 'Active Mentors', value: adminStats.activeMentors, change: `+${adminStats.mentorsThisWeek} this week`, changeType: 'positive' },
    { title: 'Students Assigned', value: adminStats.totalStudentsAssigned, change: `+${adminStats.studentsThisMonth} this month`, changeType: 'positive' },
    { title: 'Total Calls', value: callMetrics.totalCalls, change: `${callMetrics.connectedCalls} connected`, changeType: 'positive' },
    { title: 'Refund Requested', value: refundRequests.count, change: '', onClick: () => setShowRefundRequests(true) },
  ];

  const refundStudentsPaginated = React.useMemo(() => {
    const start = refundPage * REFUND_PAGE_SIZE;
    return refundRequests.students.slice(start, start + REFUND_PAGE_SIZE);
  }, [refundRequests.students, refundPage]);

  const COLORS = ['#0D9488', '#F59E0B', '#8B5CF6', '#EC4899'];

  return (
    <div className="space-y-8">
      <header className="space-y-2 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
          Welcome back, Admin
        </h1>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
          Monitor mentorship activity, review call uploads, and keep track of mentor performance.
        </p>
      </header>

      <section>
        <h2 className="sr-only">Key metrics</h2>
        <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-4 stagger-children">
          {loading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="card">
                <div className="skeleton h-4 w-24 mb-3"></div>
                <div className="skeleton h-8 w-16 mb-2"></div>
                <div className="skeleton h-3 w-20"></div>
              </div>
            ))
          ) : (
            metricCards.map((card, idx) => (
              <MetricCard key={idx} {...card} />
            ))
          )}
        </div>
      </section>

      {role === 'admin' && showRefundRequests && (
        <section className="card animate-scale-in">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Refund Requested Students</h2>
          <div className="mt-4">
            {refundRequests.students.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">No refund requests found.</p>
            ) : (
              <div className="space-y-2">
                {refundStudentsPaginated.map(student => (
                  <div 
                    key={student.id} 
                    onClick={() => navigate(`/students/${student.id}`)}
                    className="flex justify-between items-center p-3 border border-slate-200 rounded-lg dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                  >
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{student.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{student.phone}</p>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">{formatDate(student.requestDate)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {refundRequests.students.length > REFUND_PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Showing {refundPage * REFUND_PAGE_SIZE + 1} to {Math.min((refundPage + 1) * REFUND_PAGE_SIZE, refundRequests.students.length)} of {refundRequests.students.length}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setRefundPage(p => Math.max(0, p - 1))}
                  disabled={refundPage === 0}
                  className="btn btn-secondary text-sm py-1"
                >
                  Previous
                </button>
                <button
                  onClick={() => setRefundPage(p => p + 1)}
                  disabled={(refundPage + 1) * REFUND_PAGE_SIZE >= refundRequests.students.length}
                  className="btn btn-secondary text-sm py-1"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setShowRefundRequests(false)}
              className="px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition"
            >
              Close
            </button>
          </div>
        </section>
      )}

      {role === 'admin' && (
        <section className="grid gap-4 sm:gap-6 md:grid-cols-3">
          <div className="card md:col-span-2 animate-fade-in">
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white mb-4">Weekly Call Trend</h3>
            <div className="h-64">
              {loading ? (
                <div className="skeleton h-full w-full"></div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="date" stroke="var(--color-text-muted)" fontSize={12} />
                    <YAxis stroke="var(--color-text-muted)" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="calls" stroke="#0D9488" strokeWidth={2} dot={{ fill: '#0D9488' }} name="Total Calls" />
                    <Line type="monotone" dataKey="connected" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B' }} name="Connected" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="card animate-fade-in">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Students by Course</h3>
            <div className="h-64">
              {loading ? (
                <div className="skeleton h-full w-full"></div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={courseDistribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis type="number" stroke="var(--color-text-muted)" fontSize={12} />
                    <YAxis dataKey="name" type="category" stroke="var(--color-text-muted)" fontSize={12} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {courseDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>
      )}

      {role === 'admin' && (
        <section className="grid gap-4 sm:gap-6 md:grid-cols-3">
          <div className="card md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Top Performing Mentors</h3>
              <span className="text-xs text-muted">Last 7 days</span>
            </div>
            {loading ? (
              <div className="space-y-3">
                {Array(3).fill(0).map((_, i) => <div key={i} className="skeleton h-12 w-full"></div>)}
              </div>
            ) : topMentors.length > 0 ? (
              <div className="space-y-3">
                {topMentors.map((mentor, idx) => (
                  <div key={mentor.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200' :
                        idx === 1 ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' :
                        'bg-orange-50 text-orange-600 dark:bg-orange-900 dark:text-orange-200'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{mentor.full_name || mentor.email}</p>
                        <p className="text-xs text-muted">{mentor.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-brand">{mentor.callCount}</p>
                      <p className="text-xs text-muted">calls</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted text-center py-4">No call data available</p>
            )}
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Quick actions</h3>
            <ul className="space-y-4">
              {quickActions.map((action) => (
                <li key={action.label} onClick={() => navigate(action.path)} className="p-3.5 border border-slate-100 bg-slate-50 dark:border-slate-800/60 dark:bg-slate-800/60 rounded-xl hover:border-brand/30 transition cursor-pointer">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{action.label}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{action.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}

export default Dashboard;